import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import type { ScheduledTask, ScheduledTaskStatus, ScheduledTasksReport } from '../types/scheduledTasks'

/**
 * Admin diagnostics: every scheduled (beat) task, when it last ran and
 * worked, and whether it is late or failing (backend services/task_health).
 * Late = not started within its longest gap plus a grace; failing = its last
 * run raised or returned an error. Rendered as the server sends it.
 */

const STATUS: Record<ScheduledTaskStatus, { label: string; className: string }> = {
  late: { label: 'Late', className: 'bg-red-100 text-red-800' },
  failing: { label: 'Failing', className: 'bg-amber-100 text-amber-800' },
  not_seen: { label: 'Not seen yet', className: 'bg-gray-100 text-gray-600' },
  ok: { label: 'OK', className: 'bg-green-100 text-green-800' },
}

export const every = (seconds: number | null): string => {
  if (seconds === null) return '—'
  if (seconds < 120) return `${Math.round(seconds)}s`
  if (seconds < 7200) return `${Math.round(seconds / 60)} min`
  return `${Math.round(seconds / 3600)} h`
}

export const ago = (iso: string | null, now: number): string => {
  if (!iso) return '—'
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`
  return `${Math.round(minutes / 1440)} d ago`
}

const Row = ({ t, now }: { t: ScheduledTask; now: number }) => {
  const status = STATUS[t.status]
  return (
    <tr className="border-t border-gray-100 align-top" data-testid={`task-${t.entry}`}>
      <td className="px-2 py-2">
        <div className="font-medium text-gray-800">{t.entry}</div>
        {t.status === 'failing' && t.last_error && (
          <div className="mt-0.5 line-clamp-2 text-xs text-amber-700">
            {t.consecutive_failures > 1 ? `${t.consecutive_failures} in a row: ` : ''}
            {t.last_error}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-gray-600">{every(t.gap_seconds)}</td>
      <td className="px-2 py-2 text-right tabular-nums text-gray-600">{ago(t.last_started_at, now)}</td>
      <td className="px-2 py-2 text-right tabular-nums text-gray-600">{ago(t.last_success_at, now)}</td>
    </tr>
  )
}

const ScheduledTasksPanel = () => {
  const [data, setData] = useState<ScheduledTasksReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    adminAPI
      .scheduledTasks()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the scheduled tasks')))
    return () => {
      live = false
    }
  }, [])

  const now = data ? new Date(data.as_of).getTime() : Date.now()
  const late = data?.tasks.filter((t) => t.status === 'late').length ?? 0
  const failing = data?.tasks.filter((t) => t.status === 'failing').length ?? 0

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Scheduled tasks" data-testid="scheduled-tasks">
      <div className="border-b border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900">Scheduled tasks</h2>
        {data?.available && (
          <p className="mt-1 text-sm text-gray-600" data-testid="scheduled-tasks-summary">
            {late === 0 && failing === 0
              ? 'None late or failing.'
              : [late && `${late} late`, failing && `${failing} failing`].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      {error && <p className="p-4 text-sm text-red-600">{error}</p>}
      {data && !data.available && (
        <p className="p-4 text-sm text-gray-500">Redis cannot be read, so nothing is known (and no task can run).</p>
      )}
      {data?.available && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="px-2 py-2 text-left font-medium">Task</th>
                <th className="px-2 py-2 text-left font-medium">Status</th>
                <th className="px-2 py-2 text-right font-medium">Longest gap</th>
                <th className="px-2 py-2 text-right font-medium">Last started</th>
                <th className="px-2 py-2 text-right font-medium">Last worked</th>
              </tr>
            </thead>
            <tbody>
              {data.tasks.map((t) => (
                <Row key={t.task} t={t} now={now} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default ScheduledTasksPanel
